// TelefonEingabe.tsx — Telefonfelder im Format „+49 221 123456-78" für
// Stellen, die ihr eigenes Aussehen behalten: ein nacktes <input> (z. B. die
// „inp"-Felder der Akte) bzw. ein Fluent-`Input`. Die Logik steckt in
// useTelefonFeld; hier wird sie nur an das jeweilige Feld gebunden.

import * as React from "react";
import { Input, type InputProps } from "@fluentui/react-components";
import type { TelefonOptionen } from "@kmvs/forms-core";
import { useTelefonFeld } from "./useTelefonFeld";

interface TelefonBindung {
  value: string | null | undefined;
  /** Bekommt den Speicherwert („+49 221 12345"; nur „+49 " = leer). */
  onChange?: (wert: string) => void;
  /** Geschäftliche Nummer (Firma / „geschäftlich"): Durchwahl „-" erlaubt. */
  geschaeftlich?: boolean;
  /** Zentralnummer(n) der Firma — für die automatische Durchwahl. */
  zentrale?: TelefonOptionen["zentrale"];
}

type NativeProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type" | "inputMode" | "defaultValue"
>;

/** Nacktes <input> mit Telefonformat — Klasse, Platzhalter usw. wie gehabt. */
export function TelefonEingabe({
  value,
  onChange,
  geschaeftlich,
  zentrale,
  onBlur,
  onPaste,
  readOnly,
  ...rest
}: TelefonBindung & NativeProps) {
  const feld = useTelefonFeld({ value, onChange: readOnly ? undefined : onChange, geschaeftlich, zentrale });
  return (
    <input
      {...rest}
      {...feld.inputProps}
      readOnly={readOnly}
      onPaste={(e) => {
        onPaste?.(e);
        if (!readOnly && !e.defaultPrevented) feld.inputProps.onPaste(e);
      }}
      onBlur={(e) => {
        feld.inputProps.onBlur(e);
        onBlur?.(e);
      }}
    />
  );
}

type FluentProps = Omit<InputProps, "value" | "onChange" | "type" | "defaultValue">;

/** Fluent-`Input` mit Telefonformat. */
export function FluentTelefonInput({
  value,
  onChange,
  geschaeftlich,
  zentrale,
  onBlur,
  ...rest
}: TelefonBindung & FluentProps) {
  const feld = useTelefonFeld({ value, onChange, geschaeftlich, zentrale });
  const { ref, ...inputProps } = feld.inputProps;
  return (
    <Input
      {...rest}
      {...inputProps}
      input={{ ref }}
      onBlur={(e) => {
        feld.inputProps.onBlur(e);
        onBlur?.(e);
      }}
    />
  );
}
